import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Cpu, Pencil, Activity, Mic } from 'lucide-react';
import { toast } from 'react-hot-toast';
import ZonesDisplay from '@/features/workout-generator/components/ZonesDisplay';
import { IntensityTargetSelector } from '@/features/workout-generator/components/IntensityTargetSelector';
import { WorkoutActionChips } from '@/features/workout-generator/components/WorkoutActionChips';
import type {
  SportType,
  ZoneDefinition,
  IntensityTarget,
  UnitLabelKey,
  BikeIntensity,
  RunIntensity,
} from '@/types';
import type { TranslationKeys } from '@/core/i18n/translations';
import type { ZoneValidationResult } from '@/shared/utils/zoneValidation';

type Lang = 'en' | 'pl';

export interface WorkoutPromptColumnProps {
  t: TranslationKeys;
  tStr: (key: keyof TranslationKeys) => string;
  language: Lang;
  sport: SportType;
  activeIntensity: IntensityTarget;
  setBikeIntensity: (next: BikeIntensity) => void;
  setRunIntensity: (next: RunIntensity) => void;
  activeZones: ZoneDefinition[];
  updateZoneBound: (zoneId: string, field: 'min' | 'max', value: number) => void;
  unitLabelKey: UnitLabelKey;
  methodLabel: string | null;
  isCustomZones: boolean;
  zoneValidation: ZoneValidationResult;
  saveActiveZones: () => void;
  canSaveZones: boolean;
  savingZones: boolean;
  historyLoadedDateLabel: string | null;
  historyLoadedForSport: SportType | null;
  historyLoadedWorkoutName: string | null;
  promptTextareaRef: React.RefObject<HTMLTextAreaElement | null>;
  currentPrompt: string;
  setPrompts: React.Dispatch<React.SetStateAction<{ bike: string; run: string }>>;
  placeholderHintIndex: number;
  handleChipSelect: (text: string) => void;
  segmentsLength: number;
  onGenerateClick: () => void;
  isLoading: boolean;
  /** Shown under “Generating…” while async job is polling (orchestration / validation). */
  generationPhaseLabel?: string | null;
  regenerateHintPulse: boolean;
  /** GDPR / transparency: user confirms prompt will be sent to third-party AI APIs. */
  aiProcessingConsent: boolean;
  onAiProcessingConsentChange: (accepted: boolean) => void;
  /** When true, consent was stored earlier — hide the large checkbox UI. */
  aiConsentCheckboxHidden?: boolean;
  freeTierRemaining?: number | null;
  freeTierLimit?: number;
  showUpgradeHint?: boolean;
  onUpgradeClick?: () => void;
}

export function WorkoutPromptColumn({
  t,
  tStr,
  language,
  sport,
  activeIntensity,
  setBikeIntensity,
  setRunIntensity,
  activeZones,
  updateZoneBound,
  unitLabelKey,
  methodLabel,
  isCustomZones,
  zoneValidation,
  saveActiveZones,
  canSaveZones,
  savingZones,
  historyLoadedDateLabel,
  historyLoadedForSport,
  historyLoadedWorkoutName,
  promptTextareaRef,
  currentPrompt,
  setPrompts,
  placeholderHintIndex,
  handleChipSelect,
  segmentsLength,
  onGenerateClick,
  isLoading,
  generationPhaseLabel,
  regenerateHintPulse,
  aiProcessingConsent,
  onAiProcessingConsentChange,
  aiConsentCheckboxHidden = false,
  freeTierRemaining,
  freeTierLimit,
  showUpgradeHint,
  onUpgradeClick,
}: WorkoutPromptColumnProps) {
  const speechRecognitionRef = useRef<SpeechRecognition | null>(null);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [speechListening, setSpeechListening] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const w = window as typeof window & {
      SpeechRecognition?: new () => SpeechRecognition;
      webkitSpeechRecognition?: new () => SpeechRecognition;
    };
    const Ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition;
    if (!Ctor) {
      setSpeechSupported(false);
      return;
    }
    setSpeechSupported(true);
    const recognition = new Ctor();
    recognition.lang = language === 'pl' ? 'pl-PL' : 'en-US';
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.onstart = () => setSpeechListening(true);
    recognition.onend = () => setSpeechListening(false);
    recognition.onresult = (event: SpeechRecognitionEvent) => {
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const res = event.results[i];
        if (!res.isFinal) continue;
        const transcript = res[0]?.transcript ?? '';
        const piece = transcript.trim();
        if (!piece) continue;
        setPrompts((prev) => {
          const cur = prev[sport];
          const sep = cur && !/\s$/.test(cur) ? ' ' : '';
          return { ...prev, [sport]: cur + sep + piece };
        });
      }
    };
    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (event.error === 'not-allowed') {
        toast.error(
          language === 'pl'
            ? 'Nie udzielono dostępu do mikrofonu. Sprawdź uprawnienia przeglądarki.'
            : 'Microphone permission denied. Check your browser settings.',
        );
      } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
        toast.error(
          language === 'pl'
            ? 'Rozpoznawanie mowy nie powiodło się. Spróbuj ponownie.'
            : 'Speech recognition failed. Please try again.',
        );
      }
      setSpeechListening(false);
    };
    speechRecognitionRef.current = recognition;
    return () => {
      recognition.abort();
      speechRecognitionRef.current = null;
    };
  }, [language, sport, setPrompts]);

  const handleIntensityChange = (next: IntensityTarget) => {
    if (sport === 'bike') {
      // Bike never exposes 'pace' — type the callback narrowly.
      setBikeIntensity(next === 'hr' ? 'hr' : 'power');
    } else {
      setRunIntensity(next as RunIntensity);
    }
  };

  // Generation is only safe when the active zone table is fully valid: invalid bounds would
  // poison both `processAIResponseToSegments` (which averages min/max) and the Garmin export
  // (which serializes the same values into the API request body).
  const consentOk = aiConsentCheckboxHidden || aiProcessingConsent;
  const generationBlocked = isLoading || !zoneValidation.isValid || !consentOk;

  return (
    <section className="bg-white p-4 rounded-2xl border border-blue-200 shadow-xl backdrop-filter backdrop-blur-lg bg-opacity-80 dark:bg-slate-900 dark:border-slate-700 dark:bg-opacity-10">
      <h2 className="font-heading text-lg font-semibold mb-3 flex items-center gap-2">
        <Cpu className="w-5 h-5 text-orange-500" />
        {t.inputLabel}
      </h2>

      <IntensityTargetSelector
        sport={sport}
        value={activeIntensity}
        onChange={handleIntensityChange}
        t={t}
      />

      <ZonesDisplay
        zones={activeZones}
        onZoneChange={updateZoneBound}
        title={sport === 'bike' ? t.zonesTitle : t.runZonesTitle}
        language={language}
        t={t}
        unitLabelKey={unitLabelKey}
        methodLabel={methodLabel}
        isCustom={isCustomZones}
        validation={zoneValidation}
        onSave={saveActiveZones}
        canSave={canSaveZones}
        saving={savingZones}
      />

      {historyLoadedDateLabel && sport === historyLoadedForSport && (
        <div className="mt-4 mb-1 flex items-start gap-3 rounded-lg border border-blue-200 dark:border-blue-800/60 bg-blue-50 dark:bg-blue-950/40 px-3 py-2.5">
          <Pencil className="w-4 h-4 mt-0.5 shrink-0 text-blue-600 dark:text-blue-300" />
          <div className="text-xs leading-relaxed text-blue-800 dark:text-blue-200">
            <span className="font-semibold">{t.editModeBannerTitle}</span>{' '}
            <span className="font-medium">
              {historyLoadedWorkoutName ? `${historyLoadedWorkoutName} \u00B7 ` : ''}
              {historyLoadedDateLabel}
            </span>
            <span className="mx-1">.</span>
            <span className="text-blue-700/90 dark:text-blue-300/90">{t.editModeBannerHint}</span>
          </div>
        </div>
      )}

      <div className="relative mt-3">
        <textarea
          ref={promptTextareaRef}
          data-testid="workout-prompt-input"
          value={currentPrompt}
          onChange={(e) => setPrompts((p) => ({ ...p, [sport]: e.target.value }))}
          className={`w-full h-36 bg-slate-50 dark:bg-slate-950 border rounded-lg p-3 pr-12 outline-none transition-shadow font-sans ${
            historyLoadedDateLabel && sport === historyLoadedForSport
              ? 'border-blue-400 ring-2 ring-blue-200 dark:border-blue-500 dark:ring-blue-900/50'
              : 'border-slate-200'
          }`}
          placeholder={t.workoutPromptPlaceholder}
        />
        {speechSupported ? (
          <button
            type="button"
            data-testid="prompt-speech-toggle"
            title={language === 'pl' ? 'Dyktuj opis treningu (kliknij ponownie, aby zatrzymać)' : 'Dictate workout (click again to stop)'}
            aria-label={language === 'pl' ? 'Dyktuj opis treningu' : 'Dictate workout description'}
            aria-pressed={speechListening}
            onClick={() => {
              const r = speechRecognitionRef.current;
              if (!r) return;
              if (speechListening) {
                r.stop();
              } else {
                try {
                  r.start();
                } catch {
                  toast.error(
                    language === 'pl'
                      ? 'Nie można uruchomić mikrofonu. Spróbuj ponownie.'
                      : 'Could not start the microphone. Please try again.',
                  );
                }
              }
            }}
            className={`absolute bottom-2 right-2 z-10 flex h-10 w-10 items-center justify-center rounded-lg border touch-manipulation transition-colors ${
              speechListening
                ? 'border-red-500 bg-red-50 text-red-600 ring-2 ring-red-500 ring-offset-2 ring-offset-white dark:bg-red-950/50 dark:text-red-400 dark:ring-offset-slate-900 motion-safe:animate-pulse'
                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800'
            }`}
          >
            <Mic className="h-5 w-5" aria-hidden />
          </button>
        ) : null}
      </div>
      <WorkoutActionChips
        t={tStr}
        categories={sport === 'bike' ? t.workoutActionChipsBike : t.workoutActionChipsRun}
        onSelect={handleChipSelect}
        isVisible={segmentsLength > 0}
      />
      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">{sport === 'bike' ? t.example : t.runExample}</p>

      {!zoneValidation.isValid && (
        <p
          data-testid="zones-generate-blocked-hint"
          className="mt-3 text-xs font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-2 py-1.5"
        >
          {t.zonesInvalidGenerateBlocked}
        </p>
      )}

      {aiConsentCheckboxHidden ? (
        <p
          data-testid="ai-processing-disclaimer"
          className="mt-3 text-[11px] leading-snug text-slate-500 dark:text-slate-500"
        >
          {t.aiProcessingDisclaimer}{' '}
          <Link
            to="/privacy-policy"
            className="font-medium text-orange-800/95 underline underline-offset-2 decoration-orange-800 hover:text-orange-950 dark:text-orange-200/95 dark:decoration-orange-200 dark:hover:text-orange-100"
          >
            {t.aiProcessingDisclaimerLink}
          </Link>
          .
        </p>
      ) : (
        <>
          <label className="mt-3 flex items-start gap-2 cursor-pointer text-[11px] text-slate-600 dark:text-slate-400 leading-snug">
            <input
              type="checkbox"
              data-testid="ai-processing-consent"
              checked={aiProcessingConsent}
              onChange={(e) => onAiProcessingConsentChange(e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-orange-800 focus:ring-orange-600"
            />
            <span>{t.aiProcessingConsentLabel}</span>
          </label>
          <p
            data-testid="ai-processing-disclaimer"
            className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-500 leading-snug"
          >
            {t.aiProcessingDisclaimer}{' '}
            <Link
              to="/privacy-policy"
              className="font-medium text-orange-800/95 underline underline-offset-2 decoration-orange-800 hover:text-orange-950 dark:text-orange-200/95 dark:decoration-orange-200 dark:hover:text-orange-100"
            >
              {t.aiProcessingDisclaimerLink}
            </Link>
            .
          </p>
        </>
      )}

      {typeof freeTierRemaining === 'number' && typeof freeTierLimit === 'number' ? (
        <div className="mt-3 rounded-lg border border-blue-200 dark:border-blue-800/70 bg-blue-50 dark:bg-blue-950/30 px-3 py-2">
          <p className="text-xs text-blue-700 dark:text-blue-200">
            {t.freeTierRemainingLabel.replace('{remaining}', String(freeTierRemaining)).replace('{limit}', String(freeTierLimit))}
          </p>
          {showUpgradeHint ? (
            <button
              type="button"
              onClick={onUpgradeClick}
              className="mt-2 text-xs font-semibold text-orange-800 underline underline-offset-2 decoration-orange-800 dark:text-orange-200 dark:decoration-orange-200"
            >
              {t.freeTierUpgradeCta}
            </button>
          ) : null}
        </div>
      ) : null}

      <button
        type="button"
        data-testid="generate-workout-btn"
        onClick={onGenerateClick}
        disabled={generationBlocked}
        aria-disabled={generationBlocked}
        className={`mt-4 w-full min-h-[48px] py-3 rounded-lg font-bold shadow-lg transition-colors touch-manipulation ${
          generationBlocked
            ? 'bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed'
            : 'bg-orange-600 text-white hover:bg-orange-500'
        } ${
          regenerateHintPulse && !generationBlocked
            ? 'ring-4 ring-orange-300/70 dark:ring-orange-400/40 shadow-[0_0_28px_rgba(249,115,22,0.45)] motion-safe:animate-pulse'
            : ''
        }`}
      >
        {isLoading ? (
          <span className="flex flex-col items-center justify-center gap-1">
            <span className="flex items-center justify-center gap-2">
              <Activity className="animate-spin w-5 h-5" /> {t.generatingBtn}
            </span>
            {generationPhaseLabel ? (
              <span className="text-sm font-normal opacity-90">{generationPhaseLabel}</span>
            ) : null}
          </span>
        ) : (
          t.generateBtn
        )}
      </button>
    </section>
  );
}
